'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { EPlatform } from '@/utils/enums/platform-enum';
import { FC, memo, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useFlashSaleTracking } from '../hooks/useFlashSaleTracking';

// 曝光检测组件
const ExposureDetector: FC<{
  item: any;
  index: number;
  onExposure: (item: any, index: number) => void;
}> = memo(({ item, index, onExposure }) => {
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true, // 使用 triggerOnce: true，这是正确的做法
  });

  useEffect(() => {
    if (inView && item.image) {
      onExposure(item, index);
    }
  }, [inView]);

  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
});

ExposureDetector.displayName = 'ExposureDetector';

interface BottomBannerItem {
  id: number;
  image: string;
  image_alt?: string;
  jump_content?: any;
}

interface BottomBannerProps {
  bottomBannerData: BottomBannerItem[];
  deviceType: EPlatform;
}

export default function BottomBanner({ bottomBannerData, deviceType }: BottomBannerProps) {
  const isWap = deviceType !== EPlatform.WEB;

  // 使用埋点追踪hook
  const { trackBottomBannerClick, trackBottomBannerExp } = useFlashSaleTracking();

  // Banner点击处理
  const handleBannerClick = (item: BottomBannerItem, index: number) => {
    trackBottomBannerClick(item, index);
  };

  // Banner项组件
  const BannerItem = ({ item, index }: { item: BottomBannerItem; index: number }) => {
    return (
      <li className="w-full">
        <MagicJump item={item.jump_content} onBeforeJump={() => handleBannerClick(item, index)}>
          <Image
            src={item.image}
            alt={item.image_alt || 'Bottom Banner'}
            width={734}
            height={400}
            className="w-full h-auto rounded-[8px] lg:rounded-[10px] 2xl:rounded-[12px]"
          />
        </MagicJump>
      </li>
    );
  };

  if (!bottomBannerData || bottomBannerData.length === 0) {
    return null;
  }

  return (
    <ul
      className={`grid ${
        isWap
          ? 'mx-[8px] mb-[32px] gap-[8px] grid-cols-1'
          : 'mx-[80px] lg:mx-[120px] xl:mx-[180px] 2xl:mx-[220px] mb-[40px] lg:mb-[48px] xl:mb-[54px] 2xl:mb-[56px] gap-[10px] lg:gap-[12px] grid-cols-2'
      }`}
    >
      {bottomBannerData.map((item, index) => (
        <div key={index} className="relative">
          <BannerItem item={item} index={index} />
          {/* 曝光检测组件 */}
          <ExposureDetector item={item} index={index} onExposure={trackBottomBannerExp} />
        </div>
      ))}
    </ul>
  );
}
